import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useExpenses, useDeleteExpense } from '@/hooks/useExpenses';
import { usePendingReceipts, useDismissAllPendingReceipts } from '@/hooks/usePendingReceipts';
import { useBreakdown } from '@/hooks/useBreakdown';
import { useJob, useUpdateJob } from '@/hooks/useJobs';
import { ExpenseEntryFlow } from './ExpenseEntryFlow';
import { PendingReceiptsBanner } from './PendingReceiptsBanner';
import { ExpensesTable } from './ExpensesTable';
import type { ExpenseWithDetails, ExpenseSortField } from '@jobos/shared';
import { DEFAULT_EXPENSE_SORT } from '@jobos/shared';

interface JobExpensesReceiptsProps {
  jobId: string;
}

/**
 * The receipt-submission interface: the pending-receipts banner, the running
 * total + Add Expense action, the add/edit flow, and the saved-receipts table.
 * Rendered as the "Receipts" sub-tab of the Expenses tab.
 */
export function JobExpensesReceipts({ jobId }: JobExpensesReceiptsProps) {
  const { data: expenses, isLoading } = useExpenses(jobId);
  const { data: breakdown } = useBreakdown(jobId);
  const { data: pendingReceipts } = usePendingReceipts(jobId);
  const { data: job } = useJob(jobId);
  const deleteExpense = useDeleteExpense(jobId);
  const dismissAllPending = useDismissAllPendingReceipts(jobId);
  const updateJob = useUpdateJob(jobId, { showToast: false });

  // The expense sort is a per-job record: initialize the table from the job's
  // saved rule and persist any change back to the job (drives the job card order too).
  const initialSort = {
    field: (job?.expenseSortField as ExpenseSortField) ?? DEFAULT_EXPENSE_SORT.field,
    direction: (job?.expenseSortDirection as 'asc' | 'desc') ?? DEFAULT_EXPENSE_SORT.direction,
  };
  const handleSortChange = (next: { field: ExpenseSortField; direction: 'asc' | 'desc' }) => {
    updateJob.mutate({ expenseSortField: next.field, expenseSortDirection: next.direction });
  };

  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseWithDetails | null>(null);
  const [resumePending, setResumePending] = useState(false);

  const projects = breakdown?.projects?.map((p) => ({ id: p.id, name: p.name })) ?? [];
  const hasProjects = projects.length > 0;
  // Block new expenses while uploaded receipts are still unresolved, so the
  // pending pile gets cleared (resumed or dismissed) instead of accumulating.
  const hasPending = (pendingReceipts?.length ?? 0) > 0;

  const total = expenses?.reduce((sum, e) => sum + Number(e.amount), 0) ?? 0;
  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(total);

  const handleEdit = (expense: ExpenseWithDetails) => {
    setEditingExpense(expense);
    setIsAddingExpense(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this expense?')) {
      await deleteExpense.mutateAsync(id);
    }
  };

  const handleFlowComplete = () => {
    setIsAddingExpense(false);
    setEditingExpense(null);
    setResumePending(false);
  };

  const handleFlowCancel = () => {
    setIsAddingExpense(false);
    setEditingExpense(null);
    setResumePending(false);
  };

  const handleContinuePending = () => {
    setResumePending(true);
    setIsAddingExpense(true);
  };

  const handleDismissAllPending = async () => {
    const count = pendingReceipts?.length ?? 0;
    const ok = window.confirm(
      `Dismiss ${count} pending receipt${count !== 1 ? 's' : ''}? The uploaded ${count !== 1 ? 'files' : 'file'} will be permanently deleted.`
    );
    if (ok) {
      await dismissAllPending.mutateAsync();
    }
  };

  // Gate on `job` too: useClientTableSort reads initialSort only once (at mount),
  // so the table must not mount until the saved rule is available.
  if (isLoading || !job) {
    return <div className="py-8 text-center text-gray-500">Loading expenses...</div>;
  }

  return (
    <div>
      {!isAddingExpense && (
        <PendingReceiptsBanner
          pendingReceipts={pendingReceipts ?? []}
          onContinue={handleContinuePending}
          onDismissAll={handleDismissAllPending}
        />
      )}

      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0 truncate text-sm text-gray-500">
          Total: <span className={`font-semibold ${total < 0 ? 'text-green-600' : 'text-gray-900'}`}>{formattedTotal}</span>
        </div>
        {!isAddingExpense && (
          <Button
            size="sm"
            onClick={() => setIsAddingExpense(true)}
            disabled={hasPending}
            title={hasPending ? 'Resolve the pending receipts above before adding a new expense' : undefined}
            className="min-h-[44px] shrink-0 sm:min-h-0"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Expense
          </Button>
        )}
      </div>

      {isAddingExpense && (
        <ExpenseEntryFlow
          jobId={jobId}
          projects={hasProjects ? projects : undefined}
          editingExpense={editingExpense}
          pendingReceipts={resumePending ? pendingReceipts : undefined}
          onComplete={handleFlowComplete}
          onCancel={handleFlowCancel}
        />
      )}

      {/* The saved-expenses list is hidden during the add/edit flow so its
          Edit/Delete affordances can't compete with the in-progress form. */}
      {!isAddingExpense && (
        expenses && expenses.length > 0 ? (
          <ExpensesTable
            expenses={expenses}
            hasProjects={hasProjects}
            formattedTotal={formattedTotal}
            onEdit={handleEdit}
            onDelete={handleDelete}
            initialSort={initialSort}
            onSortChange={handleSortChange}
          />
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">No expenses recorded for this job.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 min-h-[44px] sm:min-h-0"
              onClick={() => setIsAddingExpense(true)}
              disabled={hasPending}
              title={hasPending ? 'Resolve the pending receipts above before adding a new expense' : undefined}
            >
              Add the first expense
            </Button>
          </div>
        )
      )}
    </div>
  );
}
